import { useEffect } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useBoundStore } from "../stores/useBoundStore";
import {
  downloadFiles,
  downloadFilesLink,
  getSurgeries,
  getSurgeryById,
} from "../dataAccess/apiService";
import { fetchNonEmptyPage } from "../dataAccess/appsync";
import {
  GetSurgeriesProcedureTypeEnum,
  Surgery,
} from "@clients/velys-infrastructure/api";
import toast from "react-hot-toast";
import { useTranslations } from "next-intl";
import { DebounceSearch } from "../helpers";

export const useGetPatientSurgeries = () => {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const procedureType = useBoundStore((state) => state.procedureType);
  const queryStringRaw = useBoundStore((state) => state.searchValue);
  const delayedQueryString = DebounceSearch(queryStringRaw, 150);
  const location = useBoundStore((state) => state.location);
  const setCases = useBoundStore((state) => state.setCases);
  const setCasesCursor = useBoundStore((state) => state.setCasesCursor);

  const patientSurgeryQuery = useInfiniteQuery({
    queryKey: [
      "patients",
      selectedOrgId,
      delayedQueryString,
      location,
      procedureType,
    ],
    queryFn: async ({ pageParam }: { pageParam: string | null }) => {
      if (!selectedOrgId) return;

      // page can come back with an empty data array but a non-null cursor.
      // use fetchNonEmptyPage so pagination depends on cursor not non-empty array
      const page = await fetchNonEmptyPage<Surgery>(async (cursor) => {
        const result = await getSurgeries(
          selectedOrgId,
          delayedQueryString === "" ? undefined : delayedQueryString,
          location?.toLocaleLowerCase() === "all" ? undefined : location,
          procedureType as GetSurgeriesProcedureTypeEnum,
          cursor
        );
        return { items: result?.data ?? [], nextToken: result?.cursor };
      }, pageParam || undefined);

      return { data: page.items, cursor: page.nextToken ?? undefined };
    },
    getNextPageParam: (lastPage) => lastPage?.cursor ?? undefined,
    enabled: !!selectedOrgId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true,
    initialPageParam: null,
    retry: 0,
  });

  useEffect(() => {
    if (!patientSurgeryQuery.data) {
      const filteredPatients: Surgery[] = [];
      setCases(procedureType, filteredPatients);
      setCasesCursor(undefined);
      return;
    }

    const allPatients = patientSurgeryQuery.data.pages.flatMap(
      (page) => page?.data ?? []
    );
    const lastPageCursor =
      patientSurgeryQuery.data.pages[patientSurgeryQuery.data.pages.length - 1]
        ?.cursor ?? undefined;

    setCases(procedureType, allPatients);
    setCasesCursor(lastPageCursor);
  }, [patientSurgeryQuery.data, setCases, setCasesCursor]);

  return patientSurgeryQuery;
};

export const useDownloadFiles = () => {
  const t = useTranslations("toast");
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const downloadRequest = {
    upload: {
      org_id: selectedOrgId!,
    },
  };
  return useMutation({
    mutationFn: async () => {
      if (!selectedOrgId) {
        throw new Error("Organization ID is missing");
      }
      return downloadFiles(selectedOrgId, downloadRequest);
    },
    onSuccess: () => {
      toast(t("downloadFiles.success"));
    },
    onError: () => {
      toast(t("downloadFiles.failure"));
    },
  });
};

export const useDownloadFilesLink = () => {
  const t = useTranslations("toast");

  return useMutation({
    mutationFn: async ({
      downloadId,
      orgId,
    }: {
      downloadId: string;
      orgId: string;
    }) => {
      if (!orgId) {
        throw new Error("Organization ID missing");
      } else if (downloadId === undefined || downloadId === "") {
        throw new Error("Download ID missing");
      }
      const downloadRequest = {
        upload: {
          org_id: orgId,
        },
      };
      return await downloadFilesLink(orgId, downloadId, downloadRequest);
    },
    onSuccess: (data) => {
      if (data!.pre_signed_url) {
        const url = data!.pre_signed_url;
        const filename = "caseData";
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    onError: (error) => {
      toast(`${t("downloadFiles.failure")}: ${error}`);
    },
  });
};

export const useGetPatientSurgeryById = (patientId: string) => {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  return useQuery({
    queryKey: ["patient", selectedOrgId, patientId],
    queryFn: () => {
      if (selectedOrgId && patientId) {
        return getSurgeryById(selectedOrgId, patientId);
      }
    },
    enabled: !!selectedOrgId && !!patientId,
  });
};
